import type { BoardObjectV2 } from "@bezpart-board/shared";
import { clonePoints, isPathObject, roundPathCoord, type PathPoint } from "./pathGeometry";
import { isSegmentObject } from "./segmentGeometry";
import { nextZIndex } from "./zIndex";
import { remapGroupIdForClone, withGroupIdMeta } from "./grouping";

// Без округления до целых: иначе копия рукописного штриха «комкается».
function offsetPoints(points: PathPoint[], dx: number, dy: number): PathPoint[] {
  return points.map(
    ([px, py]) => [roundPathCoord(px + dx), roundPathCoord(py + dy)] as PathPoint
  );
}

/** Клон объекта с новым id, смещением и zIndex поверх остальных. */
export function cloneSceneObject(
  source: BoardObjectV2,
  offset: number,
  allObjects: BoardObjectV2[],
  groupMap?: Map<string, string>
): BoardObjectV2 {
  const copy = JSON.parse(JSON.stringify(source)) as BoardObjectV2;
  copy.id = crypto.randomUUID();
  copy.x = roundPathCoord((copy.x || 0) + offset);
  copy.y = roundPathCoord((copy.y || 0) + offset);
  copy.locked = false;
  copy.zIndex = nextZIndex(allObjects);

  if (isPathObject(copy) || isSegmentObject(copy)) {
    copy.points = offsetPoints(clonePoints(copy.points), offset, offset);
    if (typeof copy.w === "number") copy.w = copy.w;
    if (typeof copy.h === "number") copy.h = copy.h;
  }

  if (groupMap) {
    const nextGroupId = remapGroupIdForClone(source, groupMap);
    const nextMeta = withGroupIdMeta(copy, nextGroupId);
    // Сервер: meta optional record — null не проходит Zod (.optional() ≠ nullable)
    if (nextMeta) copy.meta = nextMeta;
    else delete (copy as { meta?: unknown }).meta;
  }

  return copy;
}

export function cloneSceneObjects(
  sources: BoardObjectV2[],
  allObjects: BoardObjectV2[],
  offset: number
): BoardObjectV2[] {
  const clones: BoardObjectV2[] = [];
  const pool = allObjects.slice();
  const groupMap = new Map<string, string>();
  const frameMap = new Map<string, string>();

  for (const src of sources) {
    const clone = cloneSceneObject(src, offset, pool, groupMap);
    if (src.type === "frame") {
      frameMap.set(src.id, clone.id);
    }
    clones.push(clone);
    pool.push(clone);
  }

  // Если в выделении есть рамка и её дети — перепривязать frameId на клон рамки.
  // Дублирование только рамки (без детей) — ожидаемо: содержимое не копируется.
  for (const clone of clones) {
    const prevFrameId =
      typeof (clone as { frameId?: string }).frameId === "string"
        ? (clone as { frameId?: string }).frameId
        : undefined;
    if (!prevFrameId) continue;
    const nextFrameId = frameMap.get(prevFrameId);
    if (nextFrameId) {
      (clone as { frameId?: string }).frameId = nextFrameId;
    }
  }

  return clones;
}
